/**
 * Cognito auth state + flow helpers for the web app.
 *
 * Uses amazon-cognito-identity-js (SRP) for all three Admin-0 flows:
 *   1. First-login - user presents temp password → NEW_PASSWORD_REQUIRED
 *      challenge → set permanent password → MFA_SETUP challenge → scan
 *      TOTP secret + verify code → session.
 *   2. Ongoing login - password → SOFTWARE_TOKEN_MFA challenge → TOTP
 *      code → session.
 *   3. Refresh - CognitoUser.getSession transparently uses the refresh
 *      token to mint a new access/id token when the current one is near
 *      expiry.
 *
 * The AdminClient in @tilt/admin-sdk reads `getAccessToken()` on every
 * authed request, so this file is the one place the auth state lives.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type IAuthenticationCallback,
} from "amazon-cognito-identity-js";

import type { TokenStore } from "@tilt/admin-sdk";

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;

if (!userPoolId || !clientId) {
  console.warn(
    "[tilt-admin] Cognito env vars missing. Fill apps/web/.env.development.local:\n" +
      "  VITE_COGNITO_USER_POOL_ID=us-east-1_xxx\n" +
      "  VITE_COGNITO_CLIENT_ID=xxxxxxxxxxxx",
  );
}

function pool(): CognitoUserPool {
  if (!userPoolId || !clientId) {
    throw new Error("Cognito pool not configured (see env vars)");
  }
  return new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
}

function currentUser(): CognitoUser | null {
  try {
    return pool().getCurrentUser();
  } catch {
    return null;
  }
}

async function currentSession(): Promise<CognitoUserSession | null> {
  const user = currentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
}

// --------------------------------------------------------------------- //
// TokenStore - consumed by AdminClient
// --------------------------------------------------------------------- //

export const cognitoTokenStore: TokenStore = {
  async getAccessToken() {
    const session = await currentSession();
    return session?.getAccessToken().getJwtToken() ?? null;
  },
  async getIdToken() {
    const session = await currentSession();
    return session?.getIdToken().getJwtToken() ?? null;
  },
  onUnauthorized() {
    currentUser()?.signOut();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  },
};

// --------------------------------------------------------------------- //
// Login flow - called by the Login page
// --------------------------------------------------------------------- //

export type LoginChallenge =
  | { type: "NEW_PASSWORD_REQUIRED"; user: CognitoUser; userAttributes: Record<string, string> }
  | { type: "MFA_SETUP"; user: CognitoUser; secretCode: string; qrUri: string }
  | { type: "SOFTWARE_TOKEN_MFA"; user: CognitoUser };

export type LoginOutcome =
  | { status: "SUCCESS"; session: CognitoUserSession }
  | { status: "CHALLENGE"; challenge: LoginChallenge }
  | { status: "ERROR"; error: Error };

export async function signIn(email: string, password: string): Promise<LoginOutcome> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve) => {
    const callback: IAuthenticationCallback = {
      onSuccess: (session) => resolve({ status: "SUCCESS", session }),
      onFailure: (err) => resolve({ status: "ERROR", error: err }),
      newPasswordRequired: (userAttributes, _requiredAttributes) => {
        // Cognito puts email + email_verified in userAttributes; strip fields
        // Cognito doesn't want back on completeNewPasswordChallenge (email
        // must not be re-sent, it's immutable).
        const { email_verified, email, ...rest } = userAttributes;
        void email_verified;
        void email;
        resolve({
          status: "CHALLENGE",
          challenge: { type: "NEW_PASSWORD_REQUIRED", user, userAttributes: rest },
        });
      },
      mfaSetup: () => {
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            resolve({
              status: "CHALLENGE",
              challenge: {
                type: "MFA_SETUP",
                user,
                secretCode,
                qrUri: otpAuthUri(email, secretCode),
              },
            });
          },
          onFailure: (err) => resolve({ status: "ERROR", error: err }),
        });
      },
      totpRequired: () => {
        resolve({ status: "CHALLENGE", challenge: { type: "SOFTWARE_TOKEN_MFA", user } });
      },
      selectMFAType: () => {
        // Cognito asks user to choose MFA factor when multiple are enrolled.
        // Our pool only allows SOFTWARE_TOKEN_MFA, so pick it automatically.
        user.sendMFASelectionAnswer("SOFTWARE_TOKEN_MFA", callback);
      },
    };

    user.authenticateUser(authDetails, callback);
  });
}

export async function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>,
): Promise<LoginOutcome> {
  return new Promise((resolve) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve({ status: "SUCCESS", session }),
      onFailure: (err) => resolve({ status: "ERROR", error: err }),
      mfaSetup: () => {
        user.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            resolve({
              status: "CHALLENGE",
              challenge: {
                type: "MFA_SETUP",
                user,
                secretCode,
                qrUri: otpAuthUri(user.getUsername(), secretCode),
              },
            });
          },
          onFailure: (err) => resolve({ status: "ERROR", error: err }),
        });
      },
    });
  });
}

export async function verifyTotpSetup(
  user: CognitoUser,
  code: string,
  deviceName = "tilt admin",
): Promise<LoginOutcome> {
  return new Promise((resolve) => {
    user.verifySoftwareToken(code, deviceName, {
      onSuccess: () => {
        user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (prefErr) => {
          if (prefErr) {
            resolve({ status: "ERROR", error: prefErr });
            return;
          }
          // After first-time enrollment Cognito issues tokens immediately,
          // but the library stores them on the user object - re-read via
          // getSession to get a consistent CognitoUserSession.
          user.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session) {
              resolve({ status: "ERROR", error: err ?? new Error("no session") });
              return;
            }
            resolve({ status: "SUCCESS", session });
          });
        });
      },
      onFailure: (err) => resolve({ status: "ERROR", error: err }),
    });
  });
}

export async function verifyTotpChallenge(user: CognitoUser, code: string): Promise<LoginOutcome> {
  return new Promise((resolve) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ status: "SUCCESS", session }),
        onFailure: (err) => resolve({ status: "ERROR", error: err }),
      },
      "SOFTWARE_TOKEN_MFA",
    );
  });
}

export function signOut(): void {
  currentUser()?.signOut();
}

export async function hasValidSession(): Promise<boolean> {
  return (await currentSession()) !== null;
}

// --------------------------------------------------------------------- //
// Forgot password - Cognito emails a 6-digit code to the registered
// address; caller supplies code + new password to reset.
// --------------------------------------------------------------------- //

export type ForgotOutcome =
  | { status: "CODE_SENT"; destination: string }
  | { status: "ERROR"; error: Error };

/** Kick off the forgot-password flow; Cognito emails a reset code. */
export async function forgotPassword(email: string): Promise<ForgotOutcome> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  return new Promise((resolve) => {
    user.forgotPassword({
      inputVerificationCode(data: { CodeDeliveryDetails?: { Destination?: string } }) {
        resolve({
          status: "CODE_SENT",
          destination: data.CodeDeliveryDetails?.Destination ?? email,
        });
      },
      onSuccess() {
        // Fires on very old SDK paths where the code is auto-resolved;
        // we still need the user to enter the code in step 2, so treat
        // this the same as CODE_SENT.
        resolve({ status: "CODE_SENT", destination: email });
      },
      onFailure(err) {
        resolve({ status: "ERROR", error: err });
      },
    });
  });
}

export type ConfirmOutcome = { status: "SUCCESS" } | { status: "ERROR"; error: Error };

/** Complete the forgot-password flow with the emailed code + new pw. */
export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<ConfirmOutcome> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  return new Promise((resolve) => {
    user.confirmPassword(code, newPassword, {
      onSuccess() {
        resolve({ status: "SUCCESS" });
      },
      onFailure(err) {
        resolve({ status: "ERROR", error: err });
      },
    });
  });
}

// --------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------- //

/** Format the secret into an otpauth:// URI that authenticator apps scan. */
function otpAuthUri(account: string, secret: string): string {
  const issuer = encodeURIComponent("tilt admin");
  const label = encodeURIComponent(`tilt admin:${account}`);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
}
